import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p>{`npm gets its configuration values from the following sources, sorted by priority:`}</p>
    <h4>{`Command Line Flags`}</h4>
    <p>{`Putting `}<inlineCode parentName="p">{`--foo bar`}</inlineCode>{` on the command line sets the `}<inlineCode parentName="p">{`foo`}</inlineCode>{` configuration
parameter to `}<inlineCode parentName="p">{`"bar"`}</inlineCode>{`.  A `}<inlineCode parentName="p">{`--`}</inlineCode>{` argument tells the cli parser to stop
reading flags.  Using `}<inlineCode parentName="p">{`--flag`}</inlineCode>{` without specifying any value will set
the value to `}<inlineCode parentName="p">{`true`}</inlineCode>{`.`}</p>
    <p>{`Example: `}<inlineCode parentName="p">{`--flag1 --flag2`}</inlineCode>{` will set both configuration parameters
to `}<inlineCode parentName="p">{`true`}</inlineCode>{`, while `}<inlineCode parentName="p">{`--flag1 --flag2 bar`}</inlineCode>{` will set `}<inlineCode parentName="p">{`flag1`}</inlineCode>{` to `}<inlineCode parentName="p">{`true`}</inlineCode>{`,
and `}<inlineCode parentName="p">{`flag2`}</inlineCode>{` to `}<inlineCode parentName="p">{`bar`}</inlineCode>{`.  Finally, `}<inlineCode parentName="p">{`--flag1 --flag2 -- bar`}</inlineCode>{` will set
both configuration parameters to `}<inlineCode parentName="p">{`true`}</inlineCode>{`, and the `}<inlineCode parentName="p">{`bar`}</inlineCode>{` is taken
as a command argument.`}</p>
    <h4>{`Environment Variables`}</h4>
    <p>{`Any environment variables that start with `}<inlineCode parentName="p">{`npm_config_`}</inlineCode>{` will be
interpreted as a configuration parameter.  For example, putting
`}<inlineCode parentName="p">{`npm_config_foo=bar`}</inlineCode>{` in your environment will set the `}<inlineCode parentName="p">{`foo`}</inlineCode>{`
configuration parameter to `}<inlineCode parentName="p">{`bar`}</inlineCode>{`.  Any environment configurations that
are not given a value will be given the value of `}<inlineCode parentName="p">{`true`}</inlineCode>{`.  Config
values are case-insensitive, so `}<inlineCode parentName="p">{`NPM_CONFIG_FOO=bar`}</inlineCode>{` will work the
same. However, please note that inside `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/scripts"
      }}><inlineCode parentName="a">{`scripts`}</inlineCode></a>{`
npm will set its own environment variables and Node will prefer
those lowercase versions over any uppercase ones that you might set.
For details see `}<a parentName="p" {...{
        "href": "https://github.com/npm/npm/issues/14528"
      }}>{`this issue`}</a>{`.`}</p>
    <p>{`Notice that you need to use underscores instead of dashes, so `}<inlineCode parentName="p">{`--allow-same-version`}</inlineCode>{`
would become `}<inlineCode parentName="p">{`npm_config_allow_same_version=true`}</inlineCode>{`.`}</p>
    <h4>{`npmrc Files`}</h4>
    <p>{`The four relevant files are:`}</p>
    <ul>
      <li parentName="ul">{`per-project configuration file (`}<inlineCode parentName="li">{`/path/to/my/project/.npmrc`}</inlineCode>{`)`}</li>
      <li parentName="ul">{`per-user configuration file (defaults to `}<inlineCode parentName="li">{`$HOME/.npmrc`}</inlineCode>{`; configurable via CLI
option `}<inlineCode parentName="li">{`--userconfig`}</inlineCode>{` or environment variable `}<inlineCode parentName="li">{`$NPM_CONFIG_USERCONFIG`}</inlineCode>{`)`}</li>
      <li parentName="ul">{`global configuration file (defaults to `}<inlineCode parentName="li">{`$PREFIX/etc/npmrc`}</inlineCode>{`; configurable via
CLI option `}<inlineCode parentName="li">{`--globalconfig`}</inlineCode>{` or environment variable `}<inlineCode parentName="li">{`$NPM_CONFIG_GLOBALCONFIG`}</inlineCode>{`)`}</li>
      <li parentName="ul">{`npm's built-in configuration file (`}<inlineCode parentName="li">{`/path/to/npm/npmrc`}</inlineCode>{`)`}</li>
    </ul>
    <p>{`See `}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/npmrc"
      }}>{`npmrc`}</a>{` for more details.`}</p>
    <h4>{`Default Configs`}</h4>
    <p>{`Run `}<inlineCode parentName="p">{`npm config ls -l`}</inlineCode>{` to see a set of configuration parameters that are
internal to npm, and are defaults if nothing else is specified.`}</p>
    <h3>{`Shorthands and Other CLI Niceties`}</h3>
    <p>{`The following shorthands are parsed on the command-line:`}</p>
    {
      /* AUTOGENERATED CONFIG SHORTHANDS START */
    }
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`-a`}</inlineCode>{`: `}<inlineCode parentName="li">{`--all`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--enjoy-by`}</inlineCode>{`: `}<inlineCode parentName="li">{`--before`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-c`}</inlineCode>{`: `}<inlineCode parentName="li">{`--call`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--desc`}</inlineCode>{`: `}<inlineCode parentName="li">{`--description`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-f`}</inlineCode>{`: `}<inlineCode parentName="li">{`--force`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-g`}</inlineCode>{`: `}<inlineCode parentName="li">{`--global`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--iwr`}</inlineCode>{`: `}<inlineCode parentName="li">{`--include-workspace-root`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-L`}</inlineCode>{`: `}<inlineCode parentName="li">{`--location`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-d`}</inlineCode>{`: `}<inlineCode parentName="li">{`--loglevel info`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-s`}</inlineCode>{`: `}<inlineCode parentName="li">{`--loglevel silent`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--silent`}</inlineCode>{`: `}<inlineCode parentName="li">{`--loglevel silent`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--ddd`}</inlineCode>{`: `}<inlineCode parentName="li">{`--loglevel silly`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--dd`}</inlineCode>{`: `}<inlineCode parentName="li">{`--loglevel verbose`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--verbose`}</inlineCode>{`: `}<inlineCode parentName="li">{`--loglevel verbose`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-q`}</inlineCode>{`: `}<inlineCode parentName="li">{`--loglevel warn`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--quiet`}</inlineCode>{`: `}<inlineCode parentName="li">{`--loglevel warn`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-l`}</inlineCode>{`: `}<inlineCode parentName="li">{`--long`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-m`}</inlineCode>{`: `}<inlineCode parentName="li">{`--message`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--local`}</inlineCode>{`: `}<inlineCode parentName="li">{`--no-global`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-n`}</inlineCode>{`: `}<inlineCode parentName="li">{`--no-yes`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--no`}</inlineCode>{`: `}<inlineCode parentName="li">{`--no-yes`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-p`}</inlineCode>{`: `}<inlineCode parentName="li">{`--parseable`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--porcelain`}</inlineCode>{`: `}<inlineCode parentName="li">{`--parseable`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-C`}</inlineCode>{`: `}<inlineCode parentName="li">{`--prefix`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--readonly`}</inlineCode>{`: `}<inlineCode parentName="li">{`--read-only`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--reg`}</inlineCode>{`: `}<inlineCode parentName="li">{`--registry`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-S`}</inlineCode>{`: `}<inlineCode parentName="li">{`--save`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-B`}</inlineCode>{`: `}<inlineCode parentName="li">{`--save-bundle`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-D`}</inlineCode>{`: `}<inlineCode parentName="li">{`--save-dev`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-E`}</inlineCode>{`: `}<inlineCode parentName="li">{`--save-exact`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-O`}</inlineCode>{`: `}<inlineCode parentName="li">{`--save-optional`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-P`}</inlineCode>{`: `}<inlineCode parentName="li">{`--save-prod`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-?`}</inlineCode>{`: `}<inlineCode parentName="li">{`--usage`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-h`}</inlineCode>{`: `}<inlineCode parentName="li">{`--usage`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-H`}</inlineCode>{`: `}<inlineCode parentName="li">{`--usage`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--help`}</inlineCode>{`: `}<inlineCode parentName="li">{`--usage`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-v`}</inlineCode>{`: `}<inlineCode parentName="li">{`--version`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-w`}</inlineCode>{`: `}<inlineCode parentName="li">{`--workspace`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`--ws`}</inlineCode>{`: `}<inlineCode parentName="li">{`--workspaces`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`-y`}</inlineCode>{`: `}<inlineCode parentName="li">{`--yes`}</inlineCode></li>
    </ul>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    {
      /* AUTOGENERATED CONFIG SHORTHANDS END */
    }
    <p>{`If the specified configuration param resolves unambiguously to a known
configuration parameter, then it is expanded to that configuration
parameter.  For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm ls --par
# same as:
npm ls --parseable
`}</code></pre>
    <p>{`If multiple single-character shorthands are strung together, and the
resulting combination is unambiguously not some other configuration
param, then it is expanded to its various component pieces.  For
example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm ls -gpld
# same as:
npm ls --global --parseable --long --loglevel info
`}</code></pre>
    <h3>{`Config Settings`}</h3>
    {
      /* AUTOGENERATED CONFIG DESCRIPTIONS START */
    }
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`_auth`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or String`}</li>
    </ul>
    <p>{`A basic-auth string to use when authenticating against the npm registry.
This will ONLY be used to authenticate against the npm registry. For other
registries you will need to scope it like "//other-registry.tld/:_auth"`}</p>
    <p>{`Warning: This should generally not be set via a command-line option. It is
safer to use a registry-provided authentication bearer token stored in the
~/.npmrc file by running `}<inlineCode parentName="p">{`npm login`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`access`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 'restricted' for scoped packages, 'public' for unscoped packages`}</li>
      <li parentName="ul">{`Type: null, "restricted", or "public"`}</li>
    </ul>
    <p>{`When publishing scoped packages, the access level defaults to `}<inlineCode parentName="p">{`restricted`}</inlineCode>{`.
If you want your scoped package to be publicly viewable (and installable)
set `}<inlineCode parentName="p">{`--access=public`}</inlineCode>{`. The only valid values for `}<inlineCode parentName="p">{`access`}</inlineCode>{` are `}<inlineCode parentName="p">{`public`}</inlineCode>{` and
`}<inlineCode parentName="p">{`restricted`}</inlineCode>{`. Unscoped packages `}<em parentName="p">{`always`}</em>{` have an access level of `}<inlineCode parentName="p">{`public`}</inlineCode>{`.`}</p>
    <p>{`Note: Using the `}<inlineCode parentName="p">{`--access`}</inlineCode>{` flag on the `}<inlineCode parentName="p">{`npm publish`}</inlineCode>{` command will only set
the package access level on the initial publish of the package. Any
subsequent `}<inlineCode parentName="p">{`npm publish`}</inlineCode>{` commands using the `}<inlineCode parentName="p">{`--access`}</inlineCode>{` flag will not have an
effect to the access level. To make changes to the access level after the
initial publish use `}<inlineCode parentName="p">{`npm access`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`all`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`When running `}<inlineCode parentName="p">{`npm outdated`}</inlineCode>{` and `}<inlineCode parentName="p">{`npm ls`}</inlineCode>{`, setting `}<inlineCode parentName="p">{`--all`}</inlineCode>{` will show all
outdated or installed packages, rather than only those directly depended
upon by the current project.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`allow-same-version`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Prevents throwing an error when `}<inlineCode parentName="p">{`npm version`}</inlineCode>{` is used to set the new version
to the same value as the current version.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`audit`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`When "true" submit audit reports alongside the current npm command to the
default registry and all registries configured for scopes. See the
documentation for `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-audit"
      }}><inlineCode parentName="a">{`npm audit`}</inlineCode></a>{` for details on what is
submitted.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`audit-level`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null, "info", "low", "moderate", "high", "critical", or "none"`}</li>
    </ul>
    <p>{`The minimum level of vulnerability for `}<inlineCode parentName="p">{`npm audit`}</inlineCode>{` to exit with a non-zero
exit code.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`auth-type`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "legacy"`}</li>
      <li parentName="ul">{`Type: "legacy", "web", "sso", "saml", "oauth", or "webauthn"`}</li>
    </ul>
    <p>{`NOTE: auth-type values "sso", "saml", "oauth", and "webauthn" will be
removed in a future version.`}</p>
    <p>{`What authentication strategy to use with `}<inlineCode parentName="p">{`login`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`before`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or Date`}</li>
    </ul>
    <p>{`If passed to `}<inlineCode parentName="p">{`npm install`}</inlineCode>{`, will rebuild the npm tree such that only
versions that were available `}<strong parentName="p">{`on or before`}</strong>{` the `}<inlineCode parentName="p">{`--before`}</inlineCode>{` time get
installed. If there's no versions available for the current set of direct
dependencies, the command will error.`}</p>
    <p>{`If the requested version is a `}<inlineCode parentName="p">{`dist-tag`}</inlineCode>{` and the given tag does not pass the
`}<inlineCode parentName="p">{`--before`}</inlineCode>{` filter, the most recent version less than or equal to that tag
will be used. For example, `}<inlineCode parentName="p">{`foo@latest`}</inlineCode>{` might install `}<inlineCode parentName="p">{`foo@1.2`}</inlineCode>{` even though
`}<inlineCode parentName="p">{`latest`}</inlineCode>{` is `}<inlineCode parentName="p">{`2.0`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`bin-links`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Tells npm to create symlinks (or `}<inlineCode parentName="p">{`.cmd`}</inlineCode>{` shims on Windows) for package
executables.`}</p>
    <p>{`Set to false to have it not do this. This can be used to work around the
fact that some file systems don't support symlinks, even on ostensibly Unix
systems.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`browser`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: OS X: `}<inlineCode parentName="li">{`"open"`}</inlineCode>{`, Windows: `}<inlineCode parentName="li">{`"start"`}</inlineCode>{`, Others: `}<inlineCode parentName="li">{`"xdg-open"`}</inlineCode></li>
      <li parentName="ul">{`Type: null, Boolean, or String`}</li>
    </ul>
    <p>{`The browser that is called by npm commands to open websites.`}</p>
    <p>{`Set to `}<inlineCode parentName="p">{`false`}</inlineCode>{` to suppress browser behavior and instead print urls to
terminal.`}</p>
    <p>{`Set to `}<inlineCode parentName="p">{`true`}</inlineCode>{` to use default system URL opener.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`ca`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or String (can be set multiple times)`}</li>
    </ul>
    <p>{`The Certificate Authority signing certificate that is trusted for SSL
connections to the registry. Values should be in PEM format (Windows calls
it "Base-64 encoded X.509 (.CER)") with newlines replaced by the string
"\\n". For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ini"
      }}>{`ca="-----BEGIN CERTIFICATE-----\\nXXXX\\nXXXX\\n-----END CERTIFICATE-----"
`}</code></pre>
    <p>{`Set to `}<inlineCode parentName="p">{`null`}</inlineCode>{` to only allow "known" registrars, or to a specific CA cert to
trust only that specific signing authority.`}</p>
    <p>{`Multiple CAs can be trusted by specifying an array of certificates:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ini"
      }}>{`ca[]="..."
ca[]="..."
`}</code></pre>
    <p>{`See also the `}<inlineCode parentName="p">{`strict-ssl`}</inlineCode>{` config.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`cache`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: Windows: `}<inlineCode parentName="li">{`%LocalAppData%\\npm-cache`}</inlineCode>{`, Posix: `}<inlineCode parentName="li">{`~/.npm`}</inlineCode></li>
      <li parentName="ul">{`Type: Path`}</li>
    </ul>
    <p>{`The location of npm's cache directory. See `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-cache"
      }}><inlineCode parentName="a">{`npm
cache`}</inlineCode></a></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`cafile`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: Path`}</li>
    </ul>
    <p>{`A path to a file containing one or multiple Certificate Authority signing
certificates. Similar to the `}<inlineCode parentName="p">{`ca`}</inlineCode>{` setting, but allows for multiple CA's, as
well as for the CA information to be stored in a file on disk.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`call`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Optional companion option for `}<inlineCode parentName="p">{`npm exec`}</inlineCode>{`, `}<inlineCode parentName="p">{`npx`}</inlineCode>{` that allows for specifying a
custom command to be run along with the installed packages.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm exec --package yo --package generator-node --call "yo node"
`}</code></pre>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`cert`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or String`}</li>
    </ul>
    <p>{`A client certificate to pass when accessing the registry. Values should be
in PEM format (Windows calls it "Base-64 encoded X.509 (.CER)") with
newlines replaced by the string "\\n". For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ini"
      }}>{`cert="-----BEGIN CERTIFICATE-----\\nXXXX\\nXXXX\\n-----END CERTIFICATE-----"
`}</code></pre>
    <p>{`It is `}<em parentName="p">{`not`}</em>{` the path to a certificate file, though you can set a
registry-scoped "certfile" path like
"//other-registry.tld/:certfile=/path/to/cert.pem".`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`ci-name`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: The name of the current CI system, or `}<inlineCode parentName="li">{`null`}</inlineCode>{` when not on a known CI
platform.`}</li>
      <li parentName="ul">{`Type: null or String`}</li>
    </ul>
    <p>{`The name of a continuous integration system. If not set explicitly, npm will
detect the current CI environment using the
`}<a parentName="p" {...{
        "href": "http://npm.im/@npmcli/ci-detect"
      }}><inlineCode parentName="a">{`@npmcli/ci-detect`}</inlineCode></a>{` module.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`cidr`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or String (can be set multiple times)`}</li>
    </ul>
    <p>{`This is a list of CIDR address to be used when configuring limited access
tokens with the `}<inlineCode parentName="p">{`npm token create`}</inlineCode>{` command.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`color`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true unless the NO_COLOR environ is set to something other than '0'`}</li>
      <li parentName="ul">{`Type: "always" or Boolean`}</li>
    </ul>
    <p>{`If false, never shows colors. If `}<inlineCode parentName="p">{`"always"`}</inlineCode>{` then always shows colors. If
true, then only prints color codes for tty file descriptors.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`commit-hooks`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Run git commit hooks when using the `}<inlineCode parentName="p">{`npm version`}</inlineCode>{` command.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`depth`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: `}<inlineCode parentName="li">{`Infinity`}</inlineCode>{` if `}<inlineCode parentName="li">{`--all`}</inlineCode>{` is set, otherwise `}<inlineCode parentName="li">{`1`}</inlineCode></li>
      <li parentName="ul">{`Type: null or Number`}</li>
    </ul>
    <p>{`The depth to go when recursing packages for `}<inlineCode parentName="p">{`npm ls`}</inlineCode>{`.`}</p>
    <p>{`If not set, `}<inlineCode parentName="p">{`npm ls`}</inlineCode>{` will show only the immediate dependencies of the root
project. If `}<inlineCode parentName="p">{`--all`}</inlineCode>{` is set, then npm will show all dependencies by default.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`description`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Show the description in `}<inlineCode parentName="p">{`npm search`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`diff`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default:`}</li>
      <li parentName="ul">{`Type: String (can be set multiple times)`}</li>
    </ul>
    <p>{`Define arguments to compare in `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`diff-dst-prefix`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "b/"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Destination prefix to be used in `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{` output.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`diff-ignore-all-space`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Ignore whitespace when comparing lines in `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`diff-name-only`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Prints only filenames when using `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`diff-no-prefix`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Do not show any source or destination prefix in `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{` output.`}</p>
    <p>{`Note: this causes `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{` to ignore the `}<inlineCode parentName="p">{`--diff-src-prefix`}</inlineCode>{` and
`}<inlineCode parentName="p">{`--diff-dst-prefix`}</inlineCode>{` configs.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`diff-src-prefix`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "a/"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Source prefix to be used in `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{` output.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`diff-text`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Treat all files as text in `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`diff-unified`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 3`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The number of lines of context to print in `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`dry-run`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Indicates that you don't want npm to make any changes and that it should
only report what it would have done. This can be passed into any of the
commands that modify your local installation, eg, `}<inlineCode parentName="p">{`install`}</inlineCode>{`, `}<inlineCode parentName="p">{`update`}</inlineCode>{`,
`}<inlineCode parentName="p">{`dedupe`}</inlineCode>{`, `}<inlineCode parentName="p">{`uninstall`}</inlineCode>{`, as well as `}<inlineCode parentName="p">{`pack`}</inlineCode>{` and `}<inlineCode parentName="p">{`publish`}</inlineCode>{`.`}</p>
    <p>{`Note: This is NOT honored by other network related commands, eg `}<inlineCode parentName="p">{`dist-tags`}</inlineCode>{`,
`}<inlineCode parentName="p">{`owner`}</inlineCode>{`, etc.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`editor`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: The EDITOR or VISUAL environment variables, or 'notepad.exe' on
Windows, or 'vim' on Unix systems`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The command to run for `}<inlineCode parentName="p">{`npm edit`}</inlineCode>{` and `}<inlineCode parentName="p">{`npm config edit`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`engine-strict`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If set to true, then npm will stubbornly refuse to install (or even consider
installing) any package that claims to not be compatible with the current
Node.js version.`}</p>
    <p>{`This can be overridden by setting the `}<inlineCode parentName="p">{`--force`}</inlineCode>{` flag.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`fetch-retries`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 2`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The "retries" config for the `}<inlineCode parentName="p">{`retry`}</inlineCode>{` module to use when fetching packages
from the registry.`}</p>
    <p>{`npm will retry idempotent read requests to the registry in the case of
network failures or 5xx HTTP errors.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`fetch-retry-factor`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 10`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The "factor" config for the `}<inlineCode parentName="p">{`retry`}</inlineCode>{` module to use when fetching packages.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`fetch-retry-maxtimeout`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 60000 (1 minute)`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The "maxTimeout" config for the `}<inlineCode parentName="p">{`retry`}</inlineCode>{` module to use when fetching
packages.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`fetch-retry-mintimeout`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 10000 (10 seconds)`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The "minTimeout" config for the `}<inlineCode parentName="p">{`retry`}</inlineCode>{` module to use when fetching
packages.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`fetch-timeout`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 300000 (5 minutes)`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The maximum amount of time to wait for HTTP requests to complete.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`force`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Removes various protections against unfortunate side effects, common
mistakes, unnecessary performance degradation, and malicious input.`}</p>
    <ul>
      <li parentName="ul">{`Allow clobbering non-npm files in global installs.`}</li>
      <li parentName="ul">{`Allow the `}<inlineCode parentName="li">{`npm version`}</inlineCode>{` command to work on an unclean git repository.`}</li>
      <li parentName="ul">{`Allow deleting the cache folder with `}<inlineCode parentName="li">{`npm cache clean`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Allow installing packages that have an `}<inlineCode parentName="li">{`engines`}</inlineCode>{` declaration requiring a
different version of npm.`}</li>
      <li parentName="ul">{`Allow installing packages that have an `}<inlineCode parentName="li">{`engines`}</inlineCode>{` declaration requiring a
different version of `}<inlineCode parentName="li">{`node`}</inlineCode>{`, even if `}<inlineCode parentName="li">{`--engine-strict`}</inlineCode>{` is enabled.`}</li>
      <li parentName="ul">{`Allow `}<inlineCode parentName="li">{`npm audit fix`}</inlineCode>{` to install modules outside your stated dependency
range (including SemVer-major changes).`}</li>
      <li parentName="ul">{`Allow unpublishing all versions of a published package.`}</li>
      <li parentName="ul">{`Allow conflicting peerDependencies to be installed in the root project.`}</li>
      <li parentName="ul">{`Implicitly set `}<inlineCode parentName="li">{`--yes`}</inlineCode>{` during `}<inlineCode parentName="li">{`npm init`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Allow clobbering existing values in `}<inlineCode parentName="li">{`npm pkg`}</inlineCode></li>
      <li parentName="ul">{`Allow unpublishing of entire packages (not just a single version).`}</li>
    </ul>
    <p>{`If you don't have a clear idea of what you want to do, it is strongly
recommended that you do not use this option!`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`foreground-scripts`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Run all build scripts (ie, `}<inlineCode parentName="p">{`preinstall`}</inlineCode>{`, `}<inlineCode parentName="p">{`install`}</inlineCode>{`, and `}<inlineCode parentName="p">{`postinstall`}</inlineCode>{`)
scripts for installed packages in the foreground process, sharing standard
input, output, and error with the main npm process.`}</p>
    <p>{`Note that this will generally make installs run slower, and be much noisier,
but can be useful for debugging.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`format-package-lock`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Format `}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` or `}<inlineCode parentName="p">{`npm-shrinkwrap.json`}</inlineCode>{` as a human readable
file.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`fund`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`When "true" displays the message at the end of each `}<inlineCode parentName="p">{`npm install`}</inlineCode>{`
acknowledging the number of dependencies looking for funding. See `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-fund"
      }}><inlineCode parentName="a">{`npm
fund`}</inlineCode></a>{` for details.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`git`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "git"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The command to use for git commands. If git is installed on the computer,
but is not in the `}<inlineCode parentName="p">{`PATH`}</inlineCode>{`, then set this to the full path to the git binary.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`git-tag-version`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Tag the commit when using the `}<inlineCode parentName="p">{`npm version`}</inlineCode>{` command. Setting this to false
results in no commit being made at all.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`global`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Operates in "global" mode, so that packages are installed into the `}<inlineCode parentName="p">{`prefix`}</inlineCode>{`
folder instead of the current working directory. See
`}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/folders"
      }}>{`folders`}</a>{` for more on the differences in behavior.`}</p>
    <ul>
      <li parentName="ul">{`packages are installed into the `}<inlineCode parentName="li">{`{prefix}/lib/node_modules`}</inlineCode>{` folder, instead
of the current working directory.`}</li>
      <li parentName="ul">{`bin files are linked to `}<inlineCode parentName="li">{`{prefix}/bin`}</inlineCode></li>
      <li parentName="ul">{`man pages are linked to `}<inlineCode parentName="li">{`{prefix}/share/man`}</inlineCode></li>
    </ul>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`global-style`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Causes npm to install the package into your local `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder with
the same layout it uses with the global `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder. Only your
direct dependencies will show in `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` and everything they depend
on will be flattened in their `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folders. This obviously will
eliminate some deduping. If used with `}<inlineCode parentName="p">{`legacy-bundling`}</inlineCode>{`, `}<inlineCode parentName="p">{`legacy-bundling`}</inlineCode>{`
will be preferred.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`globalconfig`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: The global --prefix setting plus 'etc/npmrc'. For example,
'/usr/local/etc/npmrc'`}</li>
      <li parentName="ul">{`Type: Path`}</li>
    </ul>
    <p>{`The config file to read for global config options.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`heading`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "npm"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The string that starts all the debugging log output.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`https-proxy`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or URL`}</li>
    </ul>
    <p>{`A proxy to use for outgoing https requests. If the `}<inlineCode parentName="p">{`HTTPS_PROXY`}</inlineCode>{` or
`}<inlineCode parentName="p">{`https_proxy`}</inlineCode>{` or `}<inlineCode parentName="p">{`HTTP_PROXY`}</inlineCode>{` or `}<inlineCode parentName="p">{`http_proxy`}</inlineCode>{` environment variables are set,
proxy settings will be honored by the underlying `}<inlineCode parentName="p">{`make-fetch-happen`}</inlineCode>{`
library.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`if-present`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If true, npm will not exit with an error code when `}<inlineCode parentName="p">{`run-script`}</inlineCode>{` is invoked
for a script that isn't defined in the `}<inlineCode parentName="p">{`scripts`}</inlineCode>{` section of `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`.
This option can be used when it's desirable to optionally run a script when
it's present and fail if the script fails. This is useful, for example, when
running scripts that may only apply for some builds in an otherwise generic
CI setup.`}</p>
    <p>{`This value is not exported to the environment for child processes.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`ignore-scripts`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If true, npm does not run scripts specified in package.json files.`}</p>
    <p>{`Note that commands explicitly intended to run a particular script, such as
`}<inlineCode parentName="p">{`npm start`}</inlineCode>{`, `}<inlineCode parentName="p">{`npm stop`}</inlineCode>{`, `}<inlineCode parentName="p">{`npm restart`}</inlineCode>{`, `}<inlineCode parentName="p">{`npm test`}</inlineCode>{`, and `}<inlineCode parentName="p">{`npm run-script`}</inlineCode>{`
will still run their intended script if `}<inlineCode parentName="p">{`ignore-scripts`}</inlineCode>{` is set, but they
will `}<em parentName="p">{`not`}</em>{` run any pre- or post-scripts.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`include`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default:`}</li>
      <li parentName="ul">{`Type: "prod", "dev", "optional", or "peer" (can be set multiple times)`}</li>
    </ul>
    <p>{`Option that allows for defining which types of dependencies to install.`}</p>
    <p>{`This is the inverse of `}<inlineCode parentName="p">{`--omit=<type>`}</inlineCode>{`.`}</p>
    <p>{`Dependency types specified in `}<inlineCode parentName="p">{`--include`}</inlineCode>{` will not be omitted, regardless of
the order in which omit/include are specified on the command-line.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`include-staged`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Allow installing "staged" published packages, as defined by `}<a parentName="p" {...{
        "href": "https://github.com/npm/rfcs/pull/92"
      }}>{`npm RFC PR
#92`}</a>{`.`}</p>
    <p>{`This is experimental, and not implemented by the npm public registry.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`include-workspace-root`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Include the workspace root when workspaces are enabled for a command.`}</p>
    <p>{`When false, specifying individual workspaces via the `}<inlineCode parentName="p">{`workspace`}</inlineCode>{` config, or
all workspaces via the `}<inlineCode parentName="p">{`workspaces`}</inlineCode>{` flag, will cause npm to operate only on
the specified workspaces, and not on the root project.`}</p>
    <p>{`This value is not exported to the environment for child processes.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init-author-email`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The value `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` should use by default for the package author's email.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init-author-name`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The value `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` should use by default for the package author's name.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init-author-url`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: "" or URL`}</li>
    </ul>
    <p>{`The value `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` should use by default for the package author's
homepage.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init-license`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "ISC"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The value `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` should use by default for the package license.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init-module`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "~/.npm-init.js"`}</li>
      <li parentName="ul">{`Type: Path`}</li>
    </ul>
    <p>{`A module that will be loaded by the `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` command. See the
documentation for the
`}<a parentName="p" {...{
        "href": "https://github.com/npm/init-package-json"
      }}>{`init-package-json`}</a>{` module for
more information, or `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-init"
      }}>{`npm init`}</a>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init-version`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "1.0.0"`}</li>
      <li parentName="ul">{`Type: SemVer string`}</li>
    </ul>
    <p>{`The value that `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` should use by default for the package version
number, if not already set in package.json.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`install-links`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`When set file: protocol dependencies that exist outside of the project root
will be packed and installed as regular dependencies instead of creating a
symlink. This option has no effect on workspaces.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`json`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Whether or not to output JSON data, rather than the normal output.`}</p>
    <ul>
      <li parentName="ul">{`In `}<inlineCode parentName="li">{`npm pkg set`}</inlineCode>{` it enables parsing set values with JSON.parse() before
saving them to your `}<inlineCode parentName="li">{`package.json`}</inlineCode>{`.`}</li>
    </ul>
    <p>{`Not supported by all npm commands.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`key`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or String`}</li>
    </ul>
    <p>{`A client key to pass when accessing the registry. Values should be in PEM
format with newlines replaced by the string "\\n". For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ini"
      }}>{`key="-----BEGIN PRIVATE KEY-----\\nXXXX\\nXXXX\\n-----END PRIVATE KEY-----"
`}</code></pre>
    <p>{`It is `}<em parentName="p">{`not`}</em>{` the path to a key file, though you can set a registry-scoped
"keyfile" path like "//other-registry.tld/:keyfile=/path/to/key.pem".`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`legacy-bundling`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Causes npm to install the package such that versions of npm prior to 1.4,
such as the one included with node 0.8, can install the package. This
eliminates all automatic deduping. If used with `}<inlineCode parentName="p">{`global-style`}</inlineCode>{` this option
will be preferred.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`legacy-peer-deps`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Causes npm to completely ignore `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` when building a package
tree, as in npm versions 3 through 6.`}</p>
    <p>{`If a package cannot be installed because of overly strict `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{`
that collide, it provides a way to move forward resolving the situation.`}</p>
    <p>{`This differs from `}<inlineCode parentName="p">{`--omit=peer`}</inlineCode>{`, in that `}<inlineCode parentName="p">{`--omit=peer`}</inlineCode>{` will avoid unpacking
`}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` on disk, but will still design a tree such that
`}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` `}<em parentName="p">{`could`}</em>{` be unpacked in a correct place.`}</p>
    <p>{`Use of `}<inlineCode parentName="p">{`legacy-peer-deps`}</inlineCode>{` is not recommended, as it will not enforce the
`}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` contract that meta-dependencies may rely on.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`link`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Used with `}<inlineCode parentName="p">{`npm ls`}</inlineCode>{`, limiting output to only those packages that are linked.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`local-address`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: IP Address`}</li>
    </ul>
    <p>{`The IP address of the local interface to use when making connections to the
npm registry. Must be IPv4 in versions of Node prior to 0.12.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`location`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "user" unless `}<inlineCode parentName="li">{`--global`}</inlineCode>{` is passed, which will also set this value
to "global"`}</li>
      <li parentName="ul">{`Type: "global", "user", or "project"`}</li>
    </ul>
    <p>{`When passed to `}<inlineCode parentName="p">{`npm config`}</inlineCode>{` this refers to which config file to use.`}</p>
    <p>{`When set to "global" mode, packages are installed into the `}<inlineCode parentName="p">{`prefix`}</inlineCode>{` folder
instead of the current working directory. See
`}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/folders"
      }}>{`folders`}</a>{` for more on the differences in behavior.`}</p>
    <ul>
      <li parentName="ul">{`packages are installed into the `}<inlineCode parentName="li">{`{prefix}/lib/node_modules`}</inlineCode>{` folder, instead
of the current working directory.`}</li>
      <li parentName="ul">{`bin files are linked to `}<inlineCode parentName="li">{`{prefix}/bin`}</inlineCode></li>
      <li parentName="ul">{`man pages are linked to `}<inlineCode parentName="li">{`{prefix}/share/man`}</inlineCode></li>
    </ul>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`lockfile-version`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: Version 2 if no lockfile or current lockfile version less than or
equal to 2, otherwise maintain current lockfile version`}</li>
      <li parentName="ul">{`Type: null, 1, 2, 3, "1", "2", or "3"`}</li>
    </ul>
    <p>{`Set the lockfile format version to be used in package-lock.json and
npm-shrinkwrap-json files. Possible options are:`}</p>
    <p>{`1: The lockfile version used by npm versions 5 and 6. Lacks some data that
is used during the install, resulting in slower and possibly less
deterministic installs. Prevents lockfile churn when interoperating with
older npm versions.`}</p>
    <p>{`2: The default lockfile version used by npm version 7. Includes both the
version 1 lockfile data and version 3 lockfile data, for maximum determinism
and interoperability, at the expense of more bytes on disk.`}</p>
    <p>{`3: Only the new lockfile information introduced in npm version 7. Smaller on
disk than lockfile version 2, but not interoperable with older npm versions.
Ideal if all users are on npm version 7 and higher.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`loglevel`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "notice"`}</li>
      <li parentName="ul">{`Type: "silent", "error", "warn", "notice", "http", "timing", "info",
"verbose", or "silly"`}</li>
    </ul>
    <p>{`What level of logs to report. All logs are written to a debug log, with the
path to that file printed if the execution of a command fails.`}</p>
    <p>{`Any logs of a higher level than the setting are shown. The default is
"notice".`}</p>
    <p>{`See also the `}<inlineCode parentName="p">{`foreground-scripts`}</inlineCode>{` config.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`logs-dir`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: A directory named `}<inlineCode parentName="li">{`_logs`}</inlineCode>{` inside the cache`}</li>
      <li parentName="ul">{`Type: null or Path`}</li>
    </ul>
    <p>{`The location of npm's log directory. See `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/logging"
      }}><inlineCode parentName="a">{`npm logging`}</inlineCode></a>{`
for more information.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`logs-max`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 10`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The maximum number of log files to store.`}</p>
    <p>{`If set to 0, no log files will be written for the current run.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`long`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Show extended information in `}<inlineCode parentName="p">{`ls`}</inlineCode>{`, `}<inlineCode parentName="p">{`search`}</inlineCode>{`, and `}<inlineCode parentName="p">{`help-search`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`maxsockets`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 15`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The maximum number of connections to use per origin (protocol/host/port
combination).`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`message`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "%s"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Commit message which is used by `}<inlineCode parentName="p">{`npm version`}</inlineCode>{` when creating version commit.`}</p>
    <p>{`Any "%s" in the message will be replaced with the version number.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`node-options`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or String`}</li>
    </ul>
    <p>{`Options to pass through to Node.js via the `}<inlineCode parentName="p">{`NODE_OPTIONS`}</inlineCode>{` environment
variable. This does not impact how npm itself is executed but it does impact
how lifecycle scripts are called.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`node-version`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: Node.js `}<inlineCode parentName="li">{`process.version`}</inlineCode>{` value`}</li>
      <li parentName="ul">{`Type: SemVer string`}</li>
    </ul>
    <p>{`The node version to use when checking a package's `}<inlineCode parentName="p">{`engines`}</inlineCode>{` setting.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`noproxy`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: The value of the NO_PROXY environment variable`}</li>
      <li parentName="ul">{`Type: String (can be set multiple times)`}</li>
    </ul>
    <p>{`Domain extensions that should bypass any proxies.`}</p>
    <p>{`Also accepts a comma-delimited string.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`npm-version`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: Output of `}<inlineCode parentName="li">{`npm --version`}</inlineCode></li>
      <li parentName="ul">{`Type: SemVer string`}</li>
    </ul>
    <p>{`The npm version to use when checking a package's `}<inlineCode parentName="p">{`engines`}</inlineCode>{` setting.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`offline`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Force offline mode: no network requests will be done during install. To
allow the CLI to fill in missing cache data, see `}<inlineCode parentName="p">{`--prefer-offline`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`omit`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 'dev' if the `}<inlineCode parentName="li">{`NODE_ENV`}</inlineCode>{` environment variable is set to
'production', otherwise empty.`}</li>
      <li parentName="ul">{`Type: "dev", "optional", or "peer" (can be set multiple times)`}</li>
    </ul>
    <p>{`Dependency types to omit from the installation tree on disk.`}</p>
    <p>{`Note that these dependencies `}<em parentName="p">{`are`}</em>{` still resolved and added to the
`}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` or `}<inlineCode parentName="p">{`npm-shrinkwrap.json`}</inlineCode>{` file. They are just not
physically installed on disk.`}</p>
    <p>{`If a package type appears in both the `}<inlineCode parentName="p">{`--include`}</inlineCode>{` and `}<inlineCode parentName="p">{`--omit`}</inlineCode>{` lists, then
it will be included.`}</p>
    <p>{`If the resulting omit list includes `}<inlineCode parentName="p">{`'dev'`}</inlineCode>{`, then the `}<inlineCode parentName="p">{`NODE_ENV`}</inlineCode>{` environment
variable will be set to `}<inlineCode parentName="p">{`'production'`}</inlineCode>{` for all lifecycle scripts.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`omit-lockfile-registry-resolved`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`This option causes npm to create lock files without a `}<inlineCode parentName="p">{`resolved`}</inlineCode>{` key for
registry dependencies. Subsequent installs will need to resolve tarball
endpoints with the configured registry, likely resulting in a longer install
time.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`otp`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or String`}</li>
    </ul>
    <p>{`This is a one-time password from a two-factor authenticator. It's needed
when publishing or changing package permissions with `}<inlineCode parentName="p">{`npm access`}</inlineCode>{`.`}</p>
    <p>{`If not set, and a registry response fails with a challenge for a one-time
password, npm will prompt on the command line for one.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`pack-destination`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "."`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Directory in which `}<inlineCode parentName="p">{`npm pack`}</inlineCode>{` will save tarballs.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`package`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default:`}</li>
      <li parentName="ul">{`Type: String (can be set multiple times)`}</li>
    </ul>
    <p>{`The package or packages to install for `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-exec"
      }}><inlineCode parentName="a">{`npm exec`}</inlineCode></a></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`package-lock`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If set to false, then ignore `}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` files when installing. This
will also prevent `}<em parentName="p">{`writing`}</em>{` `}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` if `}<inlineCode parentName="p">{`save`}</inlineCode>{` is true.`}</p>
    <p>{`This configuration does not affect `}<inlineCode parentName="p">{`npm ci`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`package-lock-only`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If set to true, the current operation will only use the `}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{`,
ignoring `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{`.`}</p>
    <p>{`For `}<inlineCode parentName="p">{`update`}</inlineCode>{` this means only the `}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` will be updated,
instead of checking `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` and downloading dependencies.`}</p>
    <p>{`For `}<inlineCode parentName="p">{`list`}</inlineCode>{` this means the output will be based on the tree described by the
`}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{`, rather than the contents of `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`parseable`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Output parseable results from commands that write to standard output. For
`}<inlineCode parentName="p">{`npm search`}</inlineCode>{`, this will be tab-separated table format.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`prefer-offline`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If true, staleness checks for cached data will be bypassed, but missing data
will be requested from the server. To force full offline mode, use
`}<inlineCode parentName="p">{`--offline`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`prefer-online`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If true, staleness checks for cached data will be forced, making the CLI
look for updates immediately even for fresh package data.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`prefix`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: In global mode, the folder where the node executable is installed.
In local mode, the nearest parent folder containing either a package.json
file or a node_modules folder.`}</li>
      <li parentName="ul">{`Type: Path`}</li>
    </ul>
    <p>{`The location to install global items. If set on the command line, then it
forces non-global commands to run in the specified folder.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`preid`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The "prerelease identifier" to use as a prefix for the "prerelease" part of
a semver. Like the `}<inlineCode parentName="p">{`rc`}</inlineCode>{` in `}<inlineCode parentName="p">{`1.2.0-rc.8`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`progress`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: `}<inlineCode parentName="li">{`true`}</inlineCode>{` unless running in a known CI system`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`When set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`, npm will display a progress bar during time intensive
operations, if `}<inlineCode parentName="p">{`process.stderr`}</inlineCode>{` is a TTY.`}</p>
    <p>{`Set to `}<inlineCode parentName="p">{`false`}</inlineCode>{` to suppress the progress bar.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`proxy`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null, false, or URL`}</li>
    </ul>
    <p>{`A proxy to use for outgoing http requests. If the `}<inlineCode parentName="p">{`HTTP_PROXY`}</inlineCode>{` or
`}<inlineCode parentName="p">{`http_proxy`}</inlineCode>{` environment variables are set, proxy settings will be honored
by the underlying `}<inlineCode parentName="p">{`request`}</inlineCode>{` library.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`read-only`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`This is used to mark a token as unable to publish when configuring limited
access tokens with the `}<inlineCode parentName="p">{`npm token create`}</inlineCode>{` command.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`rebuild-bundle`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Rebuild bundled dependencies after installation.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`registry`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "`}<a parentName="li" {...{
          "href": "https://registry.npmjs.org/%22"
        }}>{`https://registry.npmjs.org/"`}</a></li>
      <li parentName="ul">{`Type: URL`}</li>
    </ul>
    <p>{`The base URL of the npm registry.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`replace-registry-host`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "npmjs"`}</li>
      <li parentName="ul">{`Type: "npmjs", "never", "always", or String`}</li>
    </ul>
    <p>{`Defines behavior for replacing the registry host in a lockfile with the
configured registry.`}</p>
    <p>{`The default behavior is to replace package dist URLs from the default
registry (`}<a parentName="p" {...{
        "href": "https://registry.npmjs.org"
      }}>{`https://registry.npmjs.org`}</a>{`) to the configured registry. If set to
"never", then use the registry value. If set to "always", then replace the
registry host with the configured host every time.`}</p>
    <p>{`You may also specify a bare hostname (e.g., "registry.npmjs.org").`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`save`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: `}<inlineCode parentName="li">{`true`}</inlineCode>{` unless when using `}<inlineCode parentName="li">{`npm update`}</inlineCode>{` where it defaults to `}<inlineCode parentName="li">{`false`}</inlineCode></li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Save installed packages to a `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file as dependencies.`}</p>
    <p>{`When used with the `}<inlineCode parentName="p">{`npm rm`}</inlineCode>{` command, removes the dependency from
`}<inlineCode parentName="p">{`package.json`}</inlineCode>{`.`}</p>
    <p>{`Will also prevent writing to `}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` if set to `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`save-bundle`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If a package would be saved at install time by the use of `}<inlineCode parentName="p">{`--save`}</inlineCode>{`,
`}<inlineCode parentName="p">{`--save-dev`}</inlineCode>{`, or `}<inlineCode parentName="p">{`--save-optional`}</inlineCode>{`, then also put it in the
`}<inlineCode parentName="p">{`bundleDependencies`}</inlineCode>{` list.`}</p>
    <p>{`Ignored if `}<inlineCode parentName="p">{`--save-peer`}</inlineCode>{` is set, since peerDependencies cannot be bundled.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`save-dev`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Save installed packages to a package.json file as `}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`save-exact`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Dependencies saved to package.json will be configured with an exact version
rather than using npm's default semver range operator.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`save-optional`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Save installed packages to a package.json file as `}<inlineCode parentName="p">{`optionalDependencies`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`save-peer`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Save installed packages to a package.json file as `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`save-prefix`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "^"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Configure how versions of packages installed to a package.json file via
`}<inlineCode parentName="p">{`--save`}</inlineCode>{` or `}<inlineCode parentName="p">{`--save-dev`}</inlineCode>{` get prefixed.`}</p>
    <p>{`For example if a package has version `}<inlineCode parentName="p">{`1.2.3`}</inlineCode>{`, by default its version is set
to `}<inlineCode parentName="p">{`^1.2.3`}</inlineCode>{` which allows minor upgrades for that package, but after `}<inlineCode parentName="p">{`npm
config set save-prefix='~'`}</inlineCode>{` it would be set to `}<inlineCode parentName="p">{`~1.2.3`}</inlineCode>{` which only allows
patch upgrades.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`save-prod`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Save installed packages into `}<inlineCode parentName="p">{`dependencies`}</inlineCode>{` specifically. This is useful if
a package already exists in `}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{` or `}<inlineCode parentName="p">{`optionalDependencies`}</inlineCode>{`, but
you want to move it to be a non-optional production dependency.`}</p>
    <p>{`This is the default behavior if `}<inlineCode parentName="p">{`--save`}</inlineCode>{` is true, and neither `}<inlineCode parentName="p">{`--save-dev`}</inlineCode>{`
or `}<inlineCode parentName="p">{`--save-optional`}</inlineCode>{` are true.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`scope`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: the scope of the current project, if any, or ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Associate an operation with a scope for a scoped registry.`}</p>
    <p>{`Useful when logging in to or out of a private registry:`}</p>
    <pre><code parentName="pre" {...{}}>{`# log in, linking the scope to the custom registry
npm login --scope=@mycorp --registry=https://registry.mycorp.com

# log out, removing the link and the auth token
npm logout --scope=@mycorp
`}</code></pre>
    <p>{`This will cause `}<inlineCode parentName="p">{`@mycorp`}</inlineCode>{` to be mapped to the registry for future
installation of packages specified according to the pattern
`}<inlineCode parentName="p">{`@mycorp/package`}</inlineCode>{`.`}</p>
    <p>{`This will also cause `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` to create a scoped package.`}</p>
    <pre><code parentName="pre" {...{}}>{`# accept all defaults, and create a package named "@foo/whatever",
# instead of just named "whatever"
npm init --scope=@foo --yes
`}</code></pre>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`script-shell`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: '/bin/sh' on POSIX systems, 'cmd.exe' on Windows`}</li>
      <li parentName="ul">{`Type: null or String`}</li>
    </ul>
    <p>{`The shell to use for scripts run with the `}<inlineCode parentName="p">{`npm exec`}</inlineCode>{`, `}<inlineCode parentName="p">{`npm run`}</inlineCode>{` and `}<inlineCode parentName="p">{`npm
init <package-spec>`}</inlineCode>{` commands.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`searchexclude`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Space-separated options that limit the results from search.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`searchlimit`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 20`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`Number of items to limit search results to. Will not apply at all to legacy
searches.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`searchopts`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Space-separated options that are always passed to search.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`searchstaleness`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 900`}</li>
      <li parentName="ul">{`Type: Number`}</li>
    </ul>
    <p>{`The age of the cache, in seconds, before another registry request is made if
using legacy search endpoint.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`shell`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: SHELL environment variable, or "bash" on Posix, or "cmd.exe" on
Windows`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The shell to run for the `}<inlineCode parentName="p">{`npm explore`}</inlineCode>{` command.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`sign-git-commit`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If set to true, then the `}<inlineCode parentName="p">{`npm version`}</inlineCode>{` command will commit the new package
version using `}<inlineCode parentName="p">{`-S`}</inlineCode>{` to add a signature.`}</p>
    <p>{`Note that git requires you to have set up GPG keys in your git configs for
this to work properly.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`sign-git-tag`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If set to true, then the `}<inlineCode parentName="p">{`npm version`}</inlineCode>{` command will tag the version using
`}<inlineCode parentName="p">{`-s`}</inlineCode>{` to add a signature.`}</p>
    <p>{`Note that git requires you to have set up GPG keys in your git configs for
this to work properly.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`strict-peer-deps`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`, and `}<inlineCode parentName="p">{`--legacy-peer-deps`}</inlineCode>{` is not set, then `}<em parentName="p">{`any`}</em>{`
conflicting `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` will be treated as an install failure, even
if npm could reasonably guess the appropriate resolution based on non-peer
dependency relationships.`}</p>
    <p>{`By default, conflicting `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` deep in the dependency graph will
be resolved using the nearest non-peer dependency specification, even if
doing so will result in some packages receiving a peer dependency outside
the range set in their package's `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` object.`}</p>
    <p>{`When such and override is performed, a warning is printed, explaining the
conflict and the packages involved. If `}<inlineCode parentName="p">{`--strict-peer-deps`}</inlineCode>{` is set, then
this warning is treated as a failure.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`strict-ssl`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Whether or not to do SSL key validation when making requests to the registry
via https.`}</p>
    <p>{`See also the `}<inlineCode parentName="p">{`ca`}</inlineCode>{` config.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`tag`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "latest"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`If you ask npm to install a package and don't tell it a specific version,
then it will install the specified tag.`}</p>
    <p>{`Also the tag that is added to the package@version specified by the `}<inlineCode parentName="p">{`npm tag`}</inlineCode>{`
command, if no explicit tag is given.`}</p>
    <p>{`When used by the `}<inlineCode parentName="p">{`npm diff`}</inlineCode>{` command, this is the tag used to fetch the
tarball that will be compared with the local files by default.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`tag-version-prefix`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "v"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`If set, alters the prefix used when tagging a new version when performing a
version increment using `}<inlineCode parentName="p">{`npm-version`}</inlineCode>{`. To remove the prefix altogether, set
it to the empty string: `}<inlineCode parentName="p">{`""`}</inlineCode>{`.`}</p>
    <p>{`Because other tools may rely on the convention that npm version tags look
like `}<inlineCode parentName="p">{`v1.0.0`}</inlineCode>{`, `}<em parentName="p">{`only use this property if it is absolutely necessary`}</em>{`. In
particular, use care when overriding this setting for public packages.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`timing`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If true, writes a debug log to `}<inlineCode parentName="p">{`logs-dir`}</inlineCode>{` and timing information to
`}<inlineCode parentName="p">{`_timing.json`}</inlineCode>{` in the cache, even if the command completes successfully.
`}<inlineCode parentName="p">{`_timing.json`}</inlineCode>{` is a newline delimited list of JSON objects.`}</p>
    <p>{`You can quickly view it with this `}<a parentName="p" {...{
        "href": "https://npm.im/json"
      }}>{`json`}</a>{` command line:
`}<inlineCode parentName="p">{`npm exec -- json -g < ~/.npm/_timing.json`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`umask`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 0`}</li>
      <li parentName="ul">{`Type: Octal numeric string in range 0000..0777 (0..511)`}</li>
    </ul>
    <p>{`The "umask" value to use when setting the file creation mode on files and
folders.`}</p>
    <p>{`Folders and executables are given a mode which is `}<inlineCode parentName="p">{`0o777`}</inlineCode>{` masked against
this value. Other files are given a mode which is `}<inlineCode parentName="p">{`0o666`}</inlineCode>{` masked against
this value.`}</p>
    <p>{`Note that the underlying system will `}<em parentName="p">{`also`}</em>{` apply its own umask value to
files and folders that are created, and npm does not circumvent this, but
rather adds the `}<inlineCode parentName="p">{`--umask`}</inlineCode>{` config to it.`}</p>
    <p>{`Thus, the effective default umask value on most POSIX systems is 0o22,
meaning that folders and executables are created with a mode of 0o755 and
other files are created with a mode of 0o644.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`unicode`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false on windows, true on mac/unix systems with a unicode locale,
as defined by the `}<inlineCode parentName="li">{`LC_ALL`}</inlineCode>{`, `}<inlineCode parentName="li">{`LC_CTYPE`}</inlineCode>{`, or `}<inlineCode parentName="li">{`LANG`}</inlineCode>{` environment variables.`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`When set to true, npm uses unicode characters in the tree output. When
false, it uses ascii characters instead of unicode glyphs.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`update-notifier`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Set to false to suppress the update notification when using an older version
of npm than the latest.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`usage`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`Show short usage output about the command specified.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`user-agent`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "npm/{npm-version} node/{node-version} {platform} {arch}
workspaces/{workspaces} {ci}"`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`Sets the User-Agent request header. The following fields are replaced with
their actual counterparts:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`{npm-version}`}</inlineCode>{` - The npm version in use`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`{node-version}`}</inlineCode>{` - The Node.js version in use`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`{platform}`}</inlineCode>{` - The value of `}<inlineCode parentName="li">{`process.platform`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`{arch}`}</inlineCode>{` - The value of `}<inlineCode parentName="li">{`process.arch`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`{workspaces}`}</inlineCode>{` - Set to `}<inlineCode parentName="li">{`true`}</inlineCode>{` if the `}<inlineCode parentName="li">{`workspaces`}</inlineCode>{` or `}<inlineCode parentName="li">{`workspace`}</inlineCode>{` options
are set.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`{ci}`}</inlineCode>{` - The value of the `}<inlineCode parentName="li">{`ci-name`}</inlineCode>{` config, if set, prefixed with `}<inlineCode parentName="li">{`ci/`}</inlineCode>{`, or
an empty string if `}<inlineCode parentName="li">{`ci-name`}</inlineCode>{` is empty.`}</li>
    </ul>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`userconfig`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "~/.npmrc"`}</li>
      <li parentName="ul">{`Type: Path`}</li>
    </ul>
    <p>{`The location of user-level configuration settings.`}</p>
    <p>{`This may be overridden by the `}<inlineCode parentName="p">{`npm_config_userconfig`}</inlineCode>{` environment variable
or the `}<inlineCode parentName="p">{`--userconfig`}</inlineCode>{` command line option, but may `}<em parentName="p">{`not`}</em>{` be overridden by
settings in the `}<inlineCode parentName="p">{`globalconfig`}</inlineCode>{` file.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`version`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If true, output the npm version and exit successfully.`}</p>
    <p>{`Only relevant when specified explicitly on the command line.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`versions`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If true, output the npm version as well as node's `}<inlineCode parentName="p">{`process.versions`}</inlineCode>{` map and
the version in the current working directory's `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file if one
exists, and exit successfully.`}</p>
    <p>{`Only relevant when specified explicitly on the command line.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`viewer`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "man" on Posix, "browser" on Windows`}</li>
      <li parentName="ul">{`Type: String`}</li>
    </ul>
    <p>{`The program to use to view help content.`}</p>
    <p>{`Set to `}<inlineCode parentName="p">{`"browser"`}</inlineCode>{` to view html help content in the default web browser.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`which`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or Number`}</li>
    </ul>
    <p>{`If there are multiple funding sources, which 1-indexed source URL to open.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`workspace`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default:`}</li>
      <li parentName="ul">{`Type: String (can be set multiple times)`}</li>
    </ul>
    <p>{`Enable running a command in the context of the configured workspaces of the
current project while filtering by running only the workspaces defined by
this configuration option.`}</p>
    <p>{`Valid values for the `}<inlineCode parentName="p">{`workspace`}</inlineCode>{` config are either:`}</p>
    <ul>
      <li parentName="ul">{`Workspace names`}</li>
      <li parentName="ul">{`Path to a workspace directory`}</li>
      <li parentName="ul">{`Path to a parent workspace directory (will result in selecting all
workspaces within that folder)`}</li>
    </ul>
    <p>{`When set for the `}<inlineCode parentName="p">{`npm init`}</inlineCode>{` command, this may be set to the folder of a
workspace which does not yet exist, to create the folder and set it up as a
brand new workspace within the project.`}</p>
    <p>{`This value is not exported to the environment for child processes.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`workspaces`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or Boolean`}</li>
    </ul>
    <p>{`Set to true to run the command in the context of `}<strong parentName="p">{`all`}</strong>{` configured
workspaces.`}</p>
    <p>{`Explicitly setting this to false will cause commands like `}<inlineCode parentName="p">{`install`}</inlineCode>{` to
ignore workspaces altogether. When not set explicitly:`}</p>
    <ul>
      <li parentName="ul">{`Commands that operate on the `}<inlineCode parentName="li">{`node_modules`}</inlineCode>{` tree (install, update, etc.)
will link workspaces into the `}<inlineCode parentName="li">{`node_modules`}</inlineCode>{` folder. - Commands that do
other things (test, exec, publish, etc.) will operate on the root project,
`}<em parentName="li">{`unless`}</em>{` one or more workspaces are specified in the `}<inlineCode parentName="li">{`workspace`}</inlineCode>{` config.`}</li>
    </ul>
    <p>{`This value is not exported to the environment for child processes.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`workspaces-update`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
    </ul>
    <p>{`If set to true, the npm cli will run an update after operations that may
possibly change the workspaces installed to the `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`yes`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or Boolean`}</li>
    </ul>
    <p>{`Automatically answer "yes" to any prompts that npm might print on the
command line.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`also`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null, "dev", or "development"`}</li>
      <li parentName="ul">{`DEPRECATED: Please use --include=dev instead.`}</li>
    </ul>
    <p>{`When set to `}<inlineCode parentName="p">{`dev`}</inlineCode>{` or `}<inlineCode parentName="p">{`development`}</inlineCode>{`, this is an alias for `}<inlineCode parentName="p">{`--include=dev`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`cache-max`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: Infinity`}</li>
      <li parentName="ul">{`Type: Number`}</li>
      <li parentName="ul">{`DEPRECATED: This option has been deprecated in favor of `}<inlineCode parentName="li">{`--prefer-online`}</inlineCode></li>
    </ul>
    <p><inlineCode parentName="p">{`--cache-max=0`}</inlineCode>{` is an alias for `}<inlineCode parentName="p">{`--prefer-online`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`cache-min`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 0`}</li>
      <li parentName="ul">{`Type: Number`}</li>
      <li parentName="ul">{`DEPRECATED: This option has been deprecated in favor of `}<inlineCode parentName="li">{`--prefer-offline`}</inlineCode>{`.`}</li>
    </ul>
    <p><inlineCode parentName="p">{`--cache-min=9999 (or bigger)`}</inlineCode>{` is an alias for `}<inlineCode parentName="p">{`--prefer-offline`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`dev`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: false`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
      <li parentName="ul">{`DEPRECATED: Please use --include=dev instead.`}</li>
    </ul>
    <p>{`Alias for `}<inlineCode parentName="p">{`--include=dev`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init.author.email`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--init-author-email`}</inlineCode>{` instead.`}</li>
    </ul>
    <p>{`Alias for `}<inlineCode parentName="p">{`--init-author-email`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init.author.name`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: String`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--init-author-name`}</inlineCode>{` instead.`}</li>
    </ul>
    <p>{`Alias for `}<inlineCode parentName="p">{`--init-author-name`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init.author.url`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: ""`}</li>
      <li parentName="ul">{`Type: "" or URL`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--init-author-url`}</inlineCode>{` instead.`}</li>
    </ul>
    <p>{`Alias for `}<inlineCode parentName="p">{`--init-author-url`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init.license`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "ISC"`}</li>
      <li parentName="ul">{`Type: String`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--init-license`}</inlineCode>{` instead.`}</li>
    </ul>
    <p>{`Alias for `}<inlineCode parentName="p">{`--init-license`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init.module`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "~/.npm-init.js"`}</li>
      <li parentName="ul">{`Type: Path`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--init-module`}</inlineCode>{` instead.`}</li>
    </ul>
    <p>{`Alias for `}<inlineCode parentName="p">{`--init-module`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`init.version`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "1.0.0"`}</li>
      <li parentName="ul">{`Type: SemVer string`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--init-version`}</inlineCode>{` instead.`}</li>
    </ul>
    <p>{`Alias for `}<inlineCode parentName="p">{`--init-version`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`only`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null, "prod", or "production"`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--omit=dev`}</inlineCode>{` to omit dev dependencies from the install.`}</li>
    </ul>
    <p>{`When set to `}<inlineCode parentName="p">{`prod`}</inlineCode>{` or `}<inlineCode parentName="p">{`production`}</inlineCode>{`, this is an alias for `}<inlineCode parentName="p">{`--omit=dev`}</inlineCode>{`.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`optional`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or Boolean`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--omit=optional`}</inlineCode>{` to exclude optional dependencies, or
`}<inlineCode parentName="li">{`--include=optional`}</inlineCode>{` to include them.`}</li>
    </ul>
    <p>{`Default value does install optional deps unless otherwise omitted.`}</p>
    <p>{`Alias for --include=optional or --omit=optional`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`production`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: null`}</li>
      <li parentName="ul">{`Type: null or Boolean`}</li>
      <li parentName="ul">{`DEPRECATED: Use `}<inlineCode parentName="li">{`--omit=dev`}</inlineCode>{` instead.`}</li>
    </ul>
    <p>{`Alias for `}<inlineCode parentName="p">{`--omit=dev`}</inlineCode></p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`shrinkwrap`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: true`}</li>
      <li parentName="ul">{`Type: Boolean`}</li>
      <li parentName="ul">{`DEPRECATED: Use the --package-lock setting instead.`}</li>
    </ul>
    <p>{`Alias for --package-lock`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`sso-poll-frequency`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: 500`}</li>
      <li parentName="ul">{`Type: Number`}</li>
      <li parentName="ul">{`DEPRECATED: The --auth-type method of SSO/SAML/OAuth will be removed in a
future version of npm in favor of web-based login.`}</li>
    </ul>
    <p>{`When used with SSO-enabled `}<inlineCode parentName="p">{`auth-type`}</inlineCode>{`s, configures how regularly the
registry should be polled while the user is completing authentication.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`sso-type`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: "oauth"`}</li>
      <li parentName="ul">{`Type: null, "oauth", or "saml"`}</li>
      <li parentName="ul">{`DEPRECATED: The --auth-type method of SSO/SAML/OAuth will be removed in a
future version of npm in favor of web-based login.`}</li>
    </ul>
    <p>{`If `}<inlineCode parentName="p">{`--auth-type=sso`}</inlineCode>{`, the type of SSO type to use.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    <h4><inlineCode parentName="h4">{`tmp`}</inlineCode></h4>
    <ul>
      <li parentName="ul">{`Default: The value returned by the Node.js `}<inlineCode parentName="li">{`os.tmpdir()`}</inlineCode>{` method
`}<a parentName="li" {...{
          "href": "https://nodejs.org/api/os.html#os_os_tmpdir"
        }}>{`https://nodejs.org/api/os.html#os_os_tmpdir`}</a></li>
      <li parentName="ul">{`Type: Path`}</li>
      <li parentName="ul">{`DEPRECATED: This setting is no longer used. npm stores temporary files in a
special location in the cache, and they are managed by
`}<a parentName="li" {...{
          "href": "http://npm.im/cacache"
        }}><inlineCode parentName="a">{`cacache`}</inlineCode></a>{`.`}</li>
    </ul>
    <p>{`Historically, the location where temporary files were stored. No longer
relevant.`}</p>
    {
      /* automatically generated, do not edit manually */
    }
    {
      /* see lib/utils/config/definitions.js */
    }
    {
      /* AUTOGENERATED CONFIG DESCRIPTIONS END */
    }
    <h3>{`See also`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-config"
        }}>{`npm config`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/npmrc"
        }}>{`npmrc`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/using-npm/scripts"
        }}>{`npm scripts`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/folders"
        }}>{`npm folders`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm"
        }}>{`npm`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      